
import { useBrand } from '../hooks/useBrand.js';
import type { Game } from '../types/index.js';
import { computeStandings } from '../services/scoring.js';

interface WinnerScreenProps {
  game: Game;
  onNewGame?: () => void;
  /** When provided, renders a gear ⚙️ button for score corrections. */
  onGearTap?: () => void;
}

/**
 * Displayed after game ends. Shows winner and final score.
 * In standalone mode, shows "New Game" button.
 */
export function WinnerScreen({ game, onNewGame, onGearTap }: WinnerScreenProps) {
  const brand = useBrand();
  const { team1Score, team2Score } = computeStandings(game.rounds);

  const winner = team1Score >= team2Score ? game.team1 : game.team2;
  const winnerColor =
    winner.id === game.team1.id ? brand.colors.scoreboard.left : brand.colors.scoreboard.right;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100dvh',
        backgroundColor: brand.colors.background,
        color: brand.colors.text,
        fontFamily: "'Inter', sans-serif",
        gap: '24px',
        padding: '32px',
        textAlign: 'center',
        containerType: 'size',
        position: 'relative',
      }}
    >
      {/* Gear button — bottom-left, same style as score entry overlays */}
      {onGearTap && (
        <button
          onClick={onGearTap}
          aria-label="Game settings"
          style={{
            position: 'absolute',
            bottom: '24px',
            left: '24px',
            width: '8cqh',
            height: '8cqh',
            minWidth: '56px',
            minHeight: '56px',
            padding: 0,
            fontSize: '3.5cqh',
            border: `2px solid ${brand.colors.text}`,
            borderRadius: '1cqh',
            background: 'transparent',
            color: brand.colors.text,
            cursor: 'pointer',
            touchAction: 'manipulation',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          ⚙️
        </button>
      )}

      <div
        style={{
          fontSize: 'clamp(4rem, 12cqh, 8rem)',
          fontWeight: 700,
          fontFamily: "'Inter', sans-serif",
          opacity: 0.75,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}
      >
        Winner!
      </div>

      <div
        style={{
          fontSize: 'clamp(5rem, 15cqh, 10rem)',
          fontWeight: 800,
          fontFamily: "'Inter', sans-serif",
          color: winnerColor,
          lineHeight: 1.1,
        }}
      >
        {winner.players.map((p) => p.name).join(' & ')}
      </div>

      <div
        style={{
          fontSize: 'clamp(3rem, 10cqh, 7rem)',
          fontWeight: 600,
          fontFamily: "'Inter', sans-serif",
          opacity: 0.85,
        }}
      >
        {team1Score} - {team2Score}
      </div>

      <div
        style={{
          fontSize: 'clamp(1.5rem, 5cqh, 3rem)',
          fontWeight: 300,
          fontFamily: "'Inter', sans-serif",
          opacity: 0.7,
        }}
      >
        ({game.rounds.length} Rounds)
      </div>

      {!game.tournamentId && onNewGame && (
        <button
          onClick={onNewGame}
          style={{
            marginTop: '16px',
            padding: '18px 48px',
            fontSize: '1.3rem',
            fontWeight: 700,
            fontFamily: "'Inter', sans-serif",
            border: 'none',
            borderRadius: '12px',
            background: brand.colors.primary,
            color: '#ffffff',
            cursor: 'pointer',
            touchAction: 'manipulation',
          }}
        >
          New Game
        </button>
      )}
    </div>
  );
}
